import { useEffect, useRef } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import useEmblaCarousel from 'embla-carousel-react';
import { useMediaQuery } from 'react-responsive';

import { BANNER_SLICE_TYPES } from 'constants/shop';
import CarouselProvider from 'contexts/CarouselProvider';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { trackEvent } from 'utils/analyticsUtils';
import twclsx from 'utils/twclsx';

import { NextButton, PrevButton, usePrevNextButtons } from './PrevNextButtons';
import useCarouselHistory from './useCarouselHistory';
import useOnSlideChange from './useOnSlideChange';
import { handleStorefrontNativeAppLink, shouldOpenLinkInNewTab } from './utils';

const BANNER_CAROUSEL_DISPLAY_TYPES = new Set([
    BANNER_SLICE_TYPES.BANNER_CAROUSEL_1,
    BANNER_SLICE_TYPES.BANNER_CAROUSEL_2,
    BANNER_SLICE_TYPES.BANNER_CAROUSEL_3,
]);

/**
 * CarouselBase
 *
 * A reusable carousel component built on embla-carousel-react that handles common
 * carousel functionality including navigation arrows, header, description, and CTA link.
 *
 * @component
 *
 * @param {Object} props
 * @param {boolean} [props.arrows=false] - Whether to display navigation arrows for the carousel
 * @param {React.ReactNode} [props.betweenContent] - Optional content rendered between the header and carousel items
 * @param {React.ReactNode} props.children - Content to display in the carousel (slides)
 * @param {Object} [props.data] - Data object containing carousel metadata
 * @param {string} [props.data.name] - Title of the carousel
 * @param {string} [props.data.heading] - Carousel name for banner carousels only (`BANNER_CAROUSEL_*`); ignored otherwise
 * @param {string} [props.data.description] - Description text for the carousel
 * @param {string} [props.data.linkTitle] - Text for optional CTA link
 * @param {string} [props.data.linkUrl] - URL for optional CTA link
 * @param {Array} [props.data.items] - Items data for the carousel
 * @param {Function} [props.onCarouselCTAClick] - Handler for CTA link click
 * @param {Function} [props.onReady] - Handler for when embla is initialized and layout stable
 * @param {Function} [props.handleOpenProductsModal] - Handler for opening the products modal
 * @param {Object} [props.styles] - Custom styles object
 * @param {string} [props.styles.itemsViewport] - Custom classes for the viewport
 * @param {boolean} [props.hasExtraItems=false] - Whether the carousel has more items than shown
 * @param {boolean} [props.centerViewportWhenAllSlidesAreVisible=false] - Centers the carousel viewport horizontally when all slides can be displayed at once without scrolling
 * @param {string} [props.slideGap='gap-10'] - Tailwind CSS gap class to apply between carousel slides
 * @param {string} [props.analyticsName] - Custom name to use for analytics tracking (defaults to data.name)
 */
function CarouselBase({
    analyticsName,
    arrows = false,
    betweenContent,
    centerViewportWhenAllSlidesAreVisible = false,
    children,
    data = { name: '', description: '', linkTitle: '', linkUrl: '', items: [] },
    handleOpenProductsModal,
    hasExtraItems = false,
    onCarouselCTAClick,
    onReady,
    slideGap = 'gap-10',
    styles = {},
}) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const { aisleID, description, displayType, eyebrow, heading, id: carouselID, linkTitle, linkUrl, name } = data;

    const isBannerCarouselDisplay = BANNER_CAROUSEL_DISPLAY_TYPES.has(displayType);

    const carouselTitle = isBannerCarouselDisplay ? heading : name;

    // Use analyticsName if provided, otherwise fall back to name that is displayed in the carousel
    const trackingName = analyticsName ?? carouselTitle;

    const productCardMetaData = {
        ...(aisleID != null && { aisleID }),
        carouselID,
        carouselName: trackingName,
        carouselType: displayType,
    };

    const openLinkInNewTab = shouldOpenLinkInNewTab(linkUrl);

    const carouselHistory = useCarouselHistory(carouselTitle ?? '');

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        skipSnaps: true,
        align: 'start',
        slidesToScroll: 'auto',
        startIndex: carouselHistory.savedSlideIndex ?? 0,
    });

    const { nextBtnDisabled, onNextButtonClick, onPrevButtonClick, prevBtnDisabled } = usePrevNextButtons(emblaApi);

    useOnSlideChange(emblaApi, (slideNumber) => {
        trackEvent('carousel click', {
            name: trackingName,
            slideNumber,
        });

        carouselHistory.saveSlideIndex(slideNumber);
    });

    useEffect(() => {
        if (emblaApi) {
            requestAnimationFrame(() => onReady?.());
        }
    }, [carouselID, emblaApi]);

    // Conditionally append "See All" slide to carousel children if applicable
    const slides =
        Array.isArray(children) && hasExtraItems && handleOpenProductsModal
            ? [
                  ...children,
                  <SeeMoreSlide
                      key="see-more-slide"
                      onClick={() => {
                          trackEvent('carousel see more click', {
                              carousel_name: trackingName,
                              carousel_id: carouselID,
                          });
                          handleOpenProductsModal({
                              isModalDeepLink: true,
                              productCardMetaData,
                          });
                      }}
                      slideSize={mobile ? '145px' : '200px'}
                  />,
              ]
            : children;

    const carouselContent = (
        <div className={twclsx('embla embla__viewport overflow-hidden', styles?.itemsViewport)} ref={emblaRef}>
            <div className={twclsx('embla__container flex', slideGap)}>{slides}</div>
        </div>
    );

    // Conditional elements
    const renderArrows = !mobile && arrows && !(prevBtnDisabled && nextBtnDisabled);
    const renderSeeAll = handleOpenProductsModal;
    const renderCTA = linkTitle && linkUrl;
    const renderHeader = carouselTitle || eyebrow || description || renderCTA || renderArrows || renderSeeAll;

    return (
        <CarouselProvider emblaApi={emblaApi}>
            {renderHeader && (
                <header className={twclsx('mb-10 space-y-5', styles?.header)}>
                    <div className={twclsx('flex items-center gap-10', eyebrow && 'items-baseline md:items-end')}>
                        <div>
                            {eyebrow && <span className="text-body-xs tracking-[0.36px] uppercase">{eyebrow}</span>}
                            {carouselTitle && <h2 className="text-body-lg font-grotesk-bold">{carouselTitle}</h2>}
                        </div>
                        {(renderSeeAll || renderArrows) && (
                            <div className="ml-auto flex gap-20">
                                {renderSeeAll && (
                                    <button
                                        type="button"
                                        className="shrink-0 text-left text-body-mobile-xs underline md:text-body-mobile-sm"
                                        onClick={() => {
                                            trackEvent('carousel view all click', {
                                                carousel_name: trackingName,
                                                carousel_id: carouselID,
                                            });
                                            handleOpenProductsModal({
                                                isModalDeepLink: false,
                                                productCardMetaData,
                                            });
                                        }}
                                    >
                                        <span className="whitespace-nowrap">View all</span>
                                    </button>
                                )}
                                {renderArrows && (
                                    <div className="mr-10 ml-auto hidden justify-end gap-10 md:mr-0 md:flex">
                                        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
                                        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {(description || renderCTA) && (
                        <p className="text-body-mobile-xs text-grey md:text-body-mobile-sm">
                            {description}{' '}
                            {renderCTA && (
                                <UniversalLink
                                    href={linkUrl}
                                    className="link font-grotesk! text-body-mobile-xs md:text-body-mobile-sm"
                                    openInNewTab={openLinkInNewTab}
                                    onClick={onCarouselCTAClick}
                                >
                                    {linkTitle}
                                </UniversalLink>
                            )}
                        </p>
                    )}
                </header>
            )}

            {betweenContent}

            {centerViewportWhenAllSlidesAreVisible ? (
                <div className="flex justify-center">{carouselContent}</div>
            ) : (
                carouselContent
            )}
        </CarouselProvider>
    );
}

export default CarouselBase;

// PARTIALS...

/**
 * SlideBase
 *
 * A base component for carousel slides that provides consistent styling and sizing.
 *
 * @component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in the slide
 * @param {string} [props.className] - Additional CSS classes to apply
 * @param {string} [props.slideFlex] - Full flex shorthand. Overrides the default `0 0 ${slideSize}` basis.
 * @param {string} props.slideSize - CSS value for the slide width (e.g., "250px", "33.33%")
 */
function SlideBase({ children, className, slideFlex, slideSize, ...restProps }) {
    return (
        <div
            className={twclsx('embla__slide min-w-0', className)}
            style={{ flex: slideFlex ?? `0 0 ${slideSize}` }}
            {...restProps}
        >
            {children}
        </div>
    );
}

/**
 * SeeMoreSlide
 *
 * A special slide displayed at the end of carousels when there are more items to show.
 * Clicking this slide triggers the handleOpenProductsModal handler.
 *
 * @component
 *
 * @param {Object} props
 * @param {Function} props.onClick - Handler for when the slide is clicked
 * @param {string} props.slideSize - CSS value for the slide width (e.g., "250px", "33.33%")
 */
function SeeMoreSlide({ onClick, slideSize }) {
    return (
        <SlideBase slideSize={slideSize}>
            <button
                type="button"
                onClick={onClick}
                className="flex h-full w-full flex-col items-center justify-center rounded-lg border border-grey-light"
                aria-label="See more"
            >
                <span className="mb-5 block font-grotesk-bold">See more</span>
                <span className="rounded-full bg-off-white p-5">
                    <ArrowRightIcon className="size-20 text-black" />
                </span>
            </button>
        </SlideBase>
    );
}

/**
 * UniversalLink
 *
 * A link component that works universally across web and native app contexts.
 * When used in a native app (detected via window.isNativeApp), it prevents default navigation
 * and sends a postMessage to ReactNativeWebView instead. In web contexts, it works as a regular anchor tag (hard navigation only).
 *
 * @component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display within the link
 * @param {string} [props.className] - CSS classes to apply to the link
 * @param {string} props.href - The URL to navigate to
 * @param {Function} [props.onClick=() => {}] - Optional callback function to execute on click
 * @param {boolean} [props.openInNewTab=false] - Whether to open the link in a new tab (web only)
 * @returns {JSX.Element} An anchor tag with appropriate handlers for both web and native environments
 *
 * @example
 * <UniversalLink
 *   href="https://example.com"
 *   className="my-link-class"
 *   openInNewTab={true}
 *   onClick={handleLinkClick}
 * >
 *   Visit Example
 * </UniversalLink>
 */
function UniversalLink({ children, className, href, onClick = () => {}, openInNewTab = false }) {
    const anchorRef = useRef();
    const { setCartOpen } = useGlobalStore();

    return (
        <a
            href={href}
            ref={anchorRef}
            className={className}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            onClick={(e) => {
                if (window.isNativeApp) {
                    e.preventDefault();
                    handleStorefrontNativeAppLink(href);
                } else if (href === '/shop/cart') {
                    e.preventDefault();
                    setCartOpen(true);
                } else {
                    onClick?.(e);
                }

                trackEvent('carousel link click', {
                    carouselLinkCTA: anchorRef.current.textContent,
                    carouselLinkURL: href,
                });
            }}
        >
            {children}
        </a>
    );
}

export { SlideBase, SeeMoreSlide, UniversalLink };
